import { and, eq, isNull } from 'drizzle-orm';
import { addMonths, format, subMonths } from 'date-fns';
import { db, runInTransaction } from '@/db/client';
import { seedDatabase } from '@/db/seed';
import {
    accounts,
    autoCategorizationRules,
    budgetCategories,
    budgets,
    categories,
    creditCards,
    financingAdjustments,
    financings,
    goalDeposits,
    goals,
    healthAnalyses,
    installments,
    invoicePayments,
    invoices,
    pendingNotifications,
    people,
    personalLoanEvents,
    personalLoans,
    recurringTransactionTags,
    recurringTransactions,
    reservaMovements,
    reservas,
    settings,
    subcategories,
    tags,
    transactionTags,
    transactions,
} from '@/db/schema';
import { generateId } from '@/lib/id';
import { recalculateAccountBalance } from '@/db/repositories/accountRepository';
import { createBudget } from '@/db/repositories/budgetRepository';
import { createFinancing } from '@/db/repositories/financingRepository';
import { addDeposit, createGoal } from '@/db/repositories/goalRepository';
import { createInstallment } from '@/db/repositories/installmentRepository';
import { getInvoiceByCardAndMonth, payInvoice, recalculateInvoiceTotal } from '@/db/repositories/invoiceRepository';
import { createPersonalLoan, recordPersonalLoanReceipt } from '@/db/repositories/personalLoanRepository';
import { createReserva, guardar } from '@/db/repositories/reservaRepository';
import * as recurringRepo from '@/db/repositories/recurringRepository';
import { createTransaction, createTransfer } from '@/db/repositories/transactionRepository';

// O seed base (F4 do i18n) cria as categorias no idioma do aparelho; o cenário
// dev procurava só pelos nomes pt-BR e estourava em base semeada em inglês
// ("Categoria obrigatória ausente no seed base: Moradia"). Os aliases cobrem o
// seed EN (assets/seed/categories.en.json); subcategoria sem par EN fica null
// (o cenário segue sem ela — lookup de sub já era tolerante).
const EN_CATEGORY_ALIASES: Record<string, string> = {
    'Moradia': 'Housing',
    'Alimentação': 'Food',
    'Transporte': 'Transport',
    'Saúde': 'Health',
    'Serviços': 'Services',
    'Assinaturas': 'Subscriptions',
    'Lazer': 'Leisure',
    'Educação': 'Education',
    'Salário': 'Salary',
    'Freelance / Autônomo': 'Freelance / Self-employed',
};

const EN_SUBCATEGORY_ALIASES: Record<string, string> = {
    'Aluguel': 'Rent',
    'Supermercado': 'Groceries',
    'Delivery (iFood, Rappi)': 'Delivery',
    'Restaurante': 'Restaurant',
    'Combustível': 'Fuel',
    'Streaming (Video)': 'Streaming',
    'CLT': 'Paycheck',
    'Farmácia': 'Medications',
};

async function getCategoryByName(name: string, type: 'expense' | 'income') {
    const [category] = await db
        .select()
        .from(categories)
        .where(and(eq(categories.name, name), eq(categories.type, type), isNull(categories.deletedAt)));

    return category ?? null;
}

async function getSubcategoryByName(categoryId: string, name: string) {
    const [subcategory] = await db
        .select()
        .from(subcategories)
        .where(and(eq(subcategories.categoryId, categoryId), eq(subcategories.name, name)));

    if (subcategory) return subcategory;

    const alias = EN_SUBCATEGORY_ALIASES[name];
    if (!alias) return null;
    const [aliased] = await db
        .select()
        .from(subcategories)
        .where(and(eq(subcategories.categoryId, categoryId), eq(subcategories.name, alias)));
    return aliased ?? null;
}

async function requireCategory(name: string, type: 'expense' | 'income') {
    const category = await getCategoryByName(name, type)
        ?? (EN_CATEGORY_ALIASES[name]
            ? await getCategoryByName(EN_CATEGORY_ALIASES[name], type)
            : null);
    if (!category) {
        throw new Error(`Categoria obrigatória ausente no seed base: ${name} (${type})`);
    }
    return category;
}

function monthDay(date: Date, day: number) {
    return `${format(date, 'yyyy-MM')}-${String(day).padStart(2, '0')}`;
}

/**
 * Apaga TODOS os dados do usuário SEM recriar os padrões de fábrica. Usado pelo bootstrap do
 * sync (entrar num espaço substitui a base local — os dados chegam dos peers, inclusive as
 * categorias; semear as de fábrica aqui duplicaria tudo). Pra reset normal, use clearDatabase.
 */
export async function wipeAllUserData() {
    // A ordem importa: com PRAGMA foreign_keys = ON, todo registro-filho precisa
    // sair ANTES do pai, senão a deleção do pai estoura ("FOREIGN KEY constraint
    // failed"). Mantenha qualquer tabela nova listada aqui — uma tabela esquecida
    // que referencie accounts/categories/transactions/etc. quebra o "Apagar dados".
    await runInTransaction(async () => {
        const tx = db;
        // Tabelas de vínculo / filhas (apagadas primeiro).
        await tx.delete(transactionTags);
        await tx.delete(recurringTransactionTags);
        await tx.delete(budgetCategories);
        await tx.delete(pendingNotifications);
        await tx.delete(goalDeposits);
        await tx.delete(invoicePayments);
        await tx.delete(financingAdjustments);
        // Empréstimos a pessoas: eventos referenciam transactions, e transactions
        // referenciam personal_loans — apagar nesta ordem evita violar a FK.
        await tx.delete(personalLoanEvents);
        // Reservas: movements -> reservas -> (accounts/credit_cards mais abaixo).
        await tx.delete(reservaMovements);
        await tx.delete(transactions);
        await tx.delete(personalLoans);
        await tx.delete(people);
        await tx.delete(installments);
        await tx.delete(invoices);
        await tx.delete(recurringTransactions);
        await tx.delete(budgets);
        await tx.delete(autoCategorizationRules);
        await tx.delete(tags);
        await tx.delete(goals);
        await tx.delete(financings);
        await tx.delete(reservas);
        await tx.delete(creditCards);
        await tx.delete(accounts);
        await tx.delete(subcategories);
        await tx.delete(categories);
        // Sem FK (cache de análises de saúde / configs) — ordem livre.
        await tx.delete(healthAnalyses);
        await tx.delete(settings);
    });
}

export async function clearDatabase() {
    await wipeAllUserData();
    await seedDatabase();

    console.log('Database reset to baseline seed.');
}

export async function loadDevData() {
    await seedDatabase();

    const now = new Date();
    const today = format(now, 'yyyy-MM-dd');
    const currentMonth = now;
    const previousMonth = subMonths(now, 1);
    const twoMonthsAgo = subMonths(now, 2);
    const nextMonth = addMonths(now, 1);

    const checkingId = generateId();
    const savingsId = generateId();
    const walletId = generateId();
    const nubankCardId = generateId();
    const interCardId = generateId();

    await db.insert(accounts).values([
        {
            id: checkingId,
            name: 'Conta Corrente',
            type: 'checking',
            institution: 'Itaú',
            initialBalance: 5200,
            currentBalance: 5200,
            color: '#1F6B74',
            icon: 'bank',
            sortOrder: 0,
        },
        {
            id: savingsId,
            name: 'Poupança',
            type: 'savings',
            // Banco DIFERENTE do da conta corrente de propósito: com duas contas no mesmo
            // banco a Home vira duas linhas com o mesmo logo e some a leitura de "várias
            // contas, um lugar só" — que é o que os prints das lojas precisam mostrar.
            institution: 'Santander',
            initialBalance: 11000,
            currentBalance: 11000,
            color: '#EC0000',
            icon: 'piggy-bank',
            sortOrder: 1,
        },
        {
            id: walletId,
            name: 'Carteira',
            type: 'wallet',
            initialBalance: 240,
            currentBalance: 240,
            color: '#D88973',
            icon: 'wallet',
            sortOrder: 2,
        },
    ]);

    await db.insert(creditCards).values([
        {
            id: nubankCardId,
            name: 'Nubank',
            brand: 'mastercard',
            institution: 'Nubank',
            lastFourDigits: '4521',
            creditLimit: 8000,
            closingDay: 17,
            dueDay: 25,
            paymentAccountId: checkingId,
            color: '#820AD1',
            icon: 'credit-card',
            sortOrder: 0,
        },
        {
            id: interCardId,
            name: 'Inter Black',
            brand: 'mastercard',
            institution: 'Banco Inter',
            lastFourDigits: '7930',
            creditLimit: 12000,
            closingDay: 5,
            dueDay: 12,
            paymentAccountId: checkingId,
            color: '#FF7A00',
            icon: 'credit-card',
            sortOrder: 1,
        },
    ]);

    const essentialTagId = generateId();
    const homeTagId = generateId();
    const workTagId = generateId();

    await db.insert(tags).values([
        { id: essentialTagId, name: 'Essencial', color: '#10B981' },
        { id: homeTagId, name: 'Casa', color: '#0EA5E9' },
        { id: workTagId, name: 'Trabalho', color: '#8B5CF6' },
    ]);

    const housing = await requireCategory('Moradia', 'expense');
    const food = await requireCategory('Alimentação', 'expense');
    const transport = await requireCategory('Transporte', 'expense');
    const health = await requireCategory('Saúde', 'expense');
    const services = await requireCategory('Serviços', 'expense');
    const subscriptions = await requireCategory('Assinaturas', 'expense');
    const leisure = await requireCategory('Lazer', 'expense');
    const education = await requireCategory('Educação', 'expense');
    const salary = await requireCategory('Salário', 'income');
    const freelance = await requireCategory('Freelance / Autônomo', 'income');

    const rentSub = await getSubcategoryByName(housing.id, 'Aluguel');
    const supermarketSub = await getSubcategoryByName(food.id, 'Supermercado');
    const deliverySub = await getSubcategoryByName(food.id, 'Delivery (iFood, Rappi)');
    const restaurantSub = await getSubcategoryByName(food.id, 'Restaurante');
    const uberSub = await getSubcategoryByName(transport.id, 'Uber / 99');
    const fuelSub = await getSubcategoryByName(transport.id, 'Combustível');
    const internetSub = await getSubcategoryByName(services.id, 'Internet');
    const energySub = await getSubcategoryByName(services.id, 'Energia');
    const streamingSub = await getSubcategoryByName(subscriptions.id, 'Streaming (Video)');
    const musicSub = await getSubcategoryByName(subscriptions.id, 'Streaming (Música)');
    const cltSub = await getSubcategoryByName(salary.id, 'CLT');
    const pharmacySub = await getSubcategoryByName(health.id, 'Farmácia');

    // ---------- Mês atual ----------

    const salaryTx = await createTransaction({
        type: 'income',
        amount: 9800,
        description: 'Salário',
        date: monthDay(currentMonth, 5),
        accountId: checkingId,
        categoryId: salary.id,
        subcategoryId: cltSub?.id,
        isConfirmed: true,
    });

    const freelanceTx = await createTransaction({
        type: 'income',
        amount: 2400,
        description: 'Projeto consultoria',
        date: monthDay(currentMonth, 22),
        accountId: checkingId,
        categoryId: freelance.id,
        isConfirmed: false,
    });

    const rentTx = await createTransaction({
        type: 'expense',
        amount: 2800,
        description: 'Aluguel',
        date: monthDay(currentMonth, 10),
        accountId: checkingId,
        categoryId: housing.id,
        subcategoryId: rentSub?.id,
        isConfirmed: true,
    });

    await createTransaction({
        type: 'expense',
        amount: 680,
        description: 'Condomínio',
        date: monthDay(currentMonth, 10),
        accountId: checkingId,
        categoryId: housing.id,
        isConfirmed: true,
    });

    await createTransaction({
        type: 'expense',
        amount: 245.3,
        description: 'Energia',
        date: monthDay(currentMonth, 18),
        accountId: checkingId,
        categoryId: services.id,
        subcategoryId: energySub?.id,
        isConfirmed: false,
    });

    await createTransaction({
        type: 'expense',
        amount: 78.4,
        description: 'Farmácia',
        date: monthDay(currentMonth, 16),
        accountId: checkingId,
        categoryId: health.id,
        subcategoryId: pharmacySub?.id,
        isConfirmed: true,
    });

    await createTransfer({
        amount: 1500,
        description: 'Reserva mensal',
        date: monthDay(currentMonth, 8),
        sourceAccountId: checkingId,
        destinationAccountId: savingsId,
    });

    await createTransaction({
        type: 'expense',
        amount: 32,
        description: 'Café',
        date: monthDay(currentMonth, 4),
        accountId: walletId,
        categoryId: food.id,
        isConfirmed: true,
    });

    // ---------- Cartão Nubank ----------

    const nubankGroceries = await createTransaction({
        type: 'expense',
        amount: 612.4,
        description: 'Supermercado Pão de Açúcar',
        date: monthDay(currentMonth, 7),
        creditCardId: nubankCardId,
        categoryId: food.id,
        subcategoryId: supermarketSub?.id,
        isConfirmed: true,
    });

    await createTransaction({
        type: 'expense',
        amount: 87.9,
        description: 'iFood',
        date: monthDay(currentMonth, 11),
        creditCardId: nubankCardId,
        categoryId: food.id,
        subcategoryId: deliverySub?.id,
        isConfirmed: true,
    });

    await createTransaction({
        type: 'expense',
        amount: 64.3,
        description: 'Uber',
        date: monthDay(currentMonth, 14),
        creditCardId: nubankCardId,
        categoryId: transport.id,
        subcategoryId: uberSub?.id,
        isConfirmed: true,
    });

    await createTransaction({
        type: 'expense',
        amount: 145.8,
        description: 'Outback',
        date: monthDay(currentMonth, 19),
        creditCardId: nubankCardId,
        categoryId: food.id,
        subcategoryId: restaurantSub?.id,
        isConfirmed: true,
    });

    await createTransaction({
        type: 'expense',
        amount: 218.9,
        description: 'Amazon',
        date: monthDay(currentMonth, 13),
        creditCardId: nubankCardId,
        categoryId: leisure.id,
        isConfirmed: true,
    });

    // ---------- Cartão Inter Black ----------

    await createTransaction({
        type: 'expense',
        amount: 280,
        description: 'Posto Shell',
        date: monthDay(currentMonth, 9),
        creditCardId: interCardId,
        categoryId: transport.id,
        subcategoryId: fuelSub?.id,
        isConfirmed: true,
    });

    await createTransaction({
        type: 'expense',
        amount: 197,
        description: 'Curso Alura',
        date: monthDay(currentMonth, 6),
        creditCardId: interCardId,
        categoryId: education.id,
        isConfirmed: true,
    });

    await createTransaction({
        type: 'expense',
        amount: 49.9,
        description: 'Spotify Família',
        date: monthDay(currentMonth, 5),
        creditCardId: interCardId,
        categoryId: subscriptions.id,
        subcategoryId: musicSub?.id,
        isConfirmed: true,
    });

    await createTransaction({
        type: 'expense',
        amount: 55.9,
        description: 'Netflix Premium',
        date: monthDay(currentMonth, 5),
        creditCardId: interCardId,
        categoryId: subscriptions.id,
        subcategoryId: streamingSub?.id,
        isConfirmed: true,
    });

    // ---------- Mês anterior (para histórico/análise) ----------

    await createTransaction({
        type: 'income',
        amount: 9800,
        description: 'Salário',
        date: monthDay(previousMonth, 5),
        accountId: checkingId,
        categoryId: salary.id,
        subcategoryId: cltSub?.id,
        isConfirmed: true,
    });

    await createTransaction({
        type: 'expense',
        amount: 2800,
        description: 'Aluguel',
        date: monthDay(previousMonth, 10),
        accountId: checkingId,
        categoryId: housing.id,
        subcategoryId: rentSub?.id,
        isConfirmed: true,
    });

    await createTransaction({
        type: 'expense',
        amount: 720,
        description: 'Condomínio',
        date: monthDay(previousMonth, 10),
        accountId: checkingId,
        categoryId: housing.id,
        isConfirmed: true,
    });

    await createTransaction({
        type: 'expense',
        amount: 198.7,
        description: 'Energia',
        date: monthDay(previousMonth, 17),
        accountId: checkingId,
        categoryId: services.id,
        subcategoryId: energySub?.id,
        isConfirmed: true,
    });

    await createTransaction({
        type: 'expense',
        amount: 540.8,
        description: 'Supermercado Pão de Açúcar',
        date: monthDay(previousMonth, 8),
        creditCardId: nubankCardId,
        categoryId: food.id,
        subcategoryId: supermarketSub?.id,
        isConfirmed: true,
    });

    await createTransaction({
        type: 'expense',
        amount: 119.5,
        description: 'iFood',
        date: monthDay(previousMonth, 12),
        creditCardId: nubankCardId,
        categoryId: food.id,
        subcategoryId: deliverySub?.id,
        isConfirmed: true,
    });

    await createTransaction({
        type: 'expense',
        amount: 92.6,
        description: '99',
        date: monthDay(previousMonth, 14),
        creditCardId: nubankCardId,
        categoryId: transport.id,
        subcategoryId: uberSub?.id,
        isConfirmed: true,
    });

    await createTransaction({
        type: 'expense',
        amount: 320,
        description: 'Posto Ipiranga',
        date: monthDay(previousMonth, 9),
        creditCardId: interCardId,
        categoryId: transport.id,
        subcategoryId: fuelSub?.id,
        isConfirmed: true,
    });

    await createTransaction({
        type: 'expense',
        amount: 168,
        description: 'Cinema + jantar',
        date: monthDay(previousMonth, 21),
        creditCardId: interCardId,
        categoryId: leisure.id,
        isConfirmed: true,
    });

    // ---------- Há dois meses (gráfico ganha curva) ----------

    await createTransaction({
        type: 'income',
        amount: 9800,
        description: 'Salário',
        date: monthDay(twoMonthsAgo, 5),
        accountId: checkingId,
        categoryId: salary.id,
        subcategoryId: cltSub?.id,
        isConfirmed: true,
    });

    await createTransaction({
        type: 'expense',
        amount: 2800,
        description: 'Aluguel',
        date: monthDay(twoMonthsAgo, 10),
        accountId: checkingId,
        categoryId: housing.id,
        subcategoryId: rentSub?.id,
        isConfirmed: true,
    });

    await createTransaction({
        type: 'expense',
        amount: 487.3,
        description: 'Supermercado Pão de Açúcar',
        date: monthDay(twoMonthsAgo, 6),
        creditCardId: nubankCardId,
        categoryId: food.id,
        subcategoryId: supermarketSub?.id,
        isConfirmed: true,
    });

    await createTransaction({
        type: 'expense',
        amount: 234,
        description: 'Posto Shell',
        date: monthDay(twoMonthsAgo, 11),
        creditCardId: interCardId,
        categoryId: transport.id,
        subcategoryId: fuelSub?.id,
        isConfirmed: true,
    });

    // ---------- Parcelamento ----------

    await createInstallment({
        description: 'Notebook trabalho',
        totalAmount: 4800,
        installmentCount: 8,
        startInstallmentNumber: 3,
        creditCardId: interCardId,
        categoryId: education.id,
        date: monthDay(currentMonth, 4),
        invoiceMonth: format(currentMonth, 'yyyy-MM'),
    });

    // ---------- Recorrências ----------

    const recurringInternetId = await recurringRepo.create({
        type: 'expense',
        amount: 129.9,
        description: 'Internet Fibra',
        frequency: 'monthly',
        startDate: monthDay(previousMonth, 12),
        accountId: checkingId,
        categoryId: services.id,
        subcategoryId: internetSub?.id ?? null,
    });
    await recurringRepo.update(recurringInternetId, {
        nextOccurrence: monthDay(currentMonth, 12),
    });

    const recurringNetflixId = await recurringRepo.create({
        type: 'expense',
        amount: 55.9,
        description: 'Netflix Premium',
        frequency: 'monthly',
        startDate: monthDay(previousMonth, 5),
        creditCardId: interCardId,
        categoryId: subscriptions.id,
        subcategoryId: streamingSub?.id ?? null,
    });
    await recurringRepo.update(recurringNetflixId, {
        nextOccurrence: monthDay(nextMonth, 5),
    });

    const recurringSpotifyId = await recurringRepo.create({
        type: 'expense',
        amount: 49.9,
        description: 'Spotify Família',
        frequency: 'monthly',
        startDate: monthDay(previousMonth, 5),
        creditCardId: interCardId,
        categoryId: subscriptions.id,
        subcategoryId: musicSub?.id ?? null,
    });
    await recurringRepo.update(recurringSpotifyId, {
        nextOccurrence: monthDay(nextMonth, 5),
    });

    // ---------- Tags ----------

    await db.insert(transactionTags).values([
        { transactionId: salaryTx.id, tagId: workTagId },
        { transactionId: rentTx.id, tagId: essentialTagId },
        { transactionId: rentTx.id, tagId: homeTagId },
        { transactionId: nubankGroceries.id, tagId: homeTagId },
        { transactionId: freelanceTx.id, tagId: workTagId },
    ]);

    // ---------- Faturas ----------

    const createdInvoices = await db.select().from(invoices);
    for (const invoice of createdInvoices) {
        await recalculateInvoiceTotal(invoice.id);
        await getInvoiceByCardAndMonth(invoice.creditCardId, invoice.referenceMonth);
    }

    const previousNubank = await getInvoiceByCardAndMonth(nubankCardId, format(previousMonth, 'yyyy-MM'));
    if (previousNubank && previousNubank.totalAmount > 0) {
        await payInvoice(previousNubank.id, checkingId, previousNubank.totalAmount, today);
    }

    const previousInter = await getInvoiceByCardAndMonth(interCardId, format(previousMonth, 'yyyy-MM'));
    if (previousInter && previousInter.totalAmount > 0) {
        await payInvoice(previousInter.id, checkingId, previousInter.totalAmount, today);
    }

    const refreshedInvoices = await db.select().from(invoices);
    for (const invoice of refreshedInvoices) {
        await getInvoiceByCardAndMonth(invoice.creditCardId, invoice.referenceMonth);
    }

    // ---------- Financiamento ----------

    await createFinancing({
        name: 'Financiamento do carro',
        institution: 'Banco Itaú',
        paymentAccountId: checkingId,
        categoryId: transport.id,
        monthlyAmount: 1200,
        totalInstallments: 24,
        nextInstallmentNumber: 13,
        nextDueDate: monthDay(nextMonth, 10),
        lastKnownBalance: 14400,
    });

    // ---------- Quem me deve (empréstimo a pessoa) ----------

    const loan = await createPersonalLoan({
        personName: 'Pedro Almeida',
        accountId: checkingId,
        amount: 1200,
        date: monthDay(previousMonth, 15),
        notes: 'Emprestei para a reforma',
    });
    // Recebimento parcial: deixa R$ 800 em aberto e cria histórico no dossiê.
    await recordPersonalLoanReceipt({
        loanId: loan.id,
        date: monthDay(currentMonth, 6),
        amount: 400,
        accountId: checkingId,
    });

    // ---------- Caixinha (reserva dentro da conta) ----------

    const reserva = await createReserva({
        accountId: checkingId,
        name: 'Reserva de emergência',
        targetAmount: 10000,
        icon: 'piggy-bank',
        color: '#10B981',
        initialAmount: 3000,
    });
    await guardar(reserva.id, 500, monthDay(currentMonth, 8), 'Aporte mensal');

    // ---------- Orçamento ----------

    await createBudget({
        name: 'Alimentação',
        icon: 'fork-knife',
        color: food.color,
        amount: 1500,
        period: 'monthly',
        categoryIds: [food.id],
    });

    // ---------- Meta ----------

    const goal = await createGoal({
        name: 'Viagem Europa',
        targetAmount: 15000,
        deadline: format(addMonths(now, 8), 'yyyy-MM-dd'),
        accountId: checkingId,
        icon: 'airplane',
        color: '#0EA5E9',
    });
    await addDeposit(goal.id, 2500, monthDay(currentMonth, 3), 'Primeiro aporte');

    await recalculateAccountBalance(checkingId);
    await recalculateAccountBalance(savingsId);
    await recalculateAccountBalance(walletId);

    console.log(
        'Dev data loaded: 3 contas, 2 cartões, transações dos últimos 3 meses, recorrências, '
        + 'parcelamento, faturas, 1 financiamento, 1 empréstimo a pessoa (parcial), 1 caixinha, '
        + '1 orçamento e 1 meta.',
    );
}
